import { useEffect } from 'react';

/**
 * On small screens, brings the field the user just tapped into the middle of the
 * viewport, so it is never left under the on-screen keyboard or off the top of a long
 * form. It fires on focus only: once a field is centred the user can scroll and type
 * freely, and the page is never pulled back.
 *
 * Mounted once, at the app root - it needs no per-form wiring.
 */

// what a user actually types into; checkboxes, buttons and the like never need centring
const TYPEABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"]';
const IGNORED_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

// opt-out for a field that manages its own scrolling: <input data-no-autocenter />
const OPT_OUT_ATTRIBUTE = 'data-no-autocenter';

const MOBILE_QUERY = '(max-width: 991.98px)';

// long enough for the on-screen keyboard to finish opening, so the field settles in the
// middle of what is left of the viewport rather than behind the keyboard
const KEYBOARD_SETTLE_MS = 300;

// React can blur and refocus a field while re-rendering; a refocus this soon after we
// centred is that, not a fresh tap, so it must not move the page again
const REFOCUS_GRACE_MS = 700;

const isTypeableField = (element: HTMLElement) => {
  if (!element.matches?.(TYPEABLE_SELECTOR)) return false;
  if (element.closest(`[${OPT_OUT_ATTRIBUTE}]`)) return false;
  if (element instanceof HTMLInputElement && IGNORED_INPUT_TYPES.has(element.type)) return false;
  return true;
};

export default function useCenterFocusedField() {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let centredField: HTMLElement | null = null;
    let centredAt = 0;

    const handleFocusIn = (event: FocusEvent) => {
      // read the width per event, so rotating the device is picked up
      if (!window.matchMedia(MOBILE_QUERY).matches) return;

      const field = event.target as HTMLElement | null;
      if (!field || !isTypeableField(field)) return;
      if (field === centredField && Date.now() - centredAt < REFOCUS_GRACE_MS) return;

      centredField = field;
      centredAt = Date.now();

      clearTimeout(timer);
      timer = setTimeout(() => {
        // the user may have tapped elsewhere while the keyboard was opening
        if (document.activeElement !== field) return;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        field.scrollIntoView({
          block: 'center',
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      }, KEYBOARD_SETTLE_MS);
    };

    // only cancels a centring that has not run yet; the field stays remembered so the
    // grace window above can tell a re-render apart from a fresh tap
    const handleFocusOut = () => clearTimeout(timer);

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
}
